class Tabs {
	constructor(el, selectedIndex = 0) {
		if (!el) return;

		this.el = el;
		this.el.tabs = this;
		this.tabTriggers = this.el.querySelectorAll('[data-tab-trigger]');
		this.tabOpeners = this.el.querySelectorAll('[data-tab-opener]');
		this.tabPanels = this.el.querySelectorAll('[data-tab-panel]');
		this.hasAnimation = this.el.hasAttribute('data-tabs-has-animation');
		this.hiddenByDefault = this.el.getAttribute('data-tabs-hidden') === 'true';

		if (this.tabTriggers.length === 0 || this.tabTriggers.length !== this.tabPanels.length) return;

		this.init(selectedIndex);
	}

	init(selectedIndex) {
		this.tabTriggersLength = this.tabTriggers.length;
		this.selectedTab = null;

		this.clickListener = this._clickEvent.bind(this);
		this.keydownListener = this._keydownEvent.bind(this);

		let firstAvailableIndex = -1;
		for (let i = 0; i < this.tabTriggersLength; i++) {
			const isDisabled = this._isTabDisabled(i);
			if (!isDisabled && firstAvailableIndex === -1) {
				firstAvailableIndex = i;
			}
		}

		for (let i = 0; i < this.tabTriggersLength; i++) {
			this.tabTriggers[i].index = i;
			const isDisabled = this._isTabDisabled(i);

			if (!isDisabled) {
				this.tabTriggers[i].addEventListener('click', this.clickListener);
				this.tabTriggers[i].addEventListener('keydown', this.keydownListener);
			} else {
				this.tabTriggers[i].setAttribute('aria-disabled', 'true');
				this.tabTriggers[i].setAttribute('tabindex', '-1');
			}

			if (this.hasAnimation || this.hiddenByDefault) {
				this._hide(i, false);
			} else if (i === selectedIndex && !isDisabled) {
				this.selectedTab = i;
				this._show(i, false);
			} else if (i === firstAvailableIndex && (this.selectedTab === null || this._isTabDisabled(selectedIndex))) {
				this.selectedTab = i;
				this._show(i, false);
			} else {
				this._hide(i, false);
			}
		}

		this.tabOpeners.forEach((opener) => {
			opener.addEventListener('click', () => {
				const id = opener.getAttribute('data-tab-opener');
				this.selectTab(id);
			});
		});

		this.el.classList.add('is-initialized');
	}

	_isTabDisabled(index) {
		const tabTrigger = this.tabTriggers[index];
		const parentItem = tabTrigger.closest('li') || tabTrigger.parentElement;
		return parentItem && parentItem.classList.contains('_disabled');
	}

	_clickEvent(e) {
		e.preventDefault();
		const index = e.currentTarget.index;

		if (this._isTabDisabled(index)) {
			return;
		}

		if (this.hasAnimation) {
			if (this.selectedTab === index) {
				this._hide(index);
				this.selectedTab = null;
			} else {
				if (this.selectedTab !== null) this._hide(this.selectedTab);
				this._show(index, true);
				this.selectedTab = index;
			}
		} else {
			if (this.hiddenByDefault && this.selectedTab === index) {
				this._hide(index);
				this.selectedTab = null;
			} else {
				this.selectTab(index, true);
			}
		}
	}

	_keydownEvent(e) {
		let targetIndex;
		if (![37, 39].includes(e.keyCode)) return;

		e.preventDefault();
		const currentIndex = e.currentTarget.index;

		if (e.keyCode === 37) {
			for (let i = currentIndex - 1; i >= 0; i--) {
				if (!this._isTabDisabled(i)) {
					targetIndex = i;
					break;
				}
			}
		}

		if (e.keyCode === 39) {
			for (let i = currentIndex + 1; i < this.tabTriggersLength; i++) {
				if (!this._isTabDisabled(i)) {
					targetIndex = i;
					break;
				}
			}
		}

		if (targetIndex != null) this.selectTab(targetIndex, true);
	}

	_animatePanel(panel, open = true) {
		panel.style.transition = 'height 0.3s ease, padding-top 0.3s ease';
		panel.style.overflow = 'hidden';

		if (open) {
			panel.classList.remove('_hidden');
			panel.style.opacity = 1;

			const originalPaddingTop = getComputedStyle(panel).paddingTop;
			panel.style.paddingTop = '0px';
			const contentHeight = panel.scrollHeight;
			panel.style.paddingTop = originalPaddingTop;

			const paddingTop = parseInt(originalPaddingTop, 10) || 0;
			const totalHeight = contentHeight + paddingTop;

			panel.style.height = '0px';
			panel.style.paddingTop = '0px';

			requestAnimationFrame(() => {
				panel.style.height = contentHeight + 'px';
				panel.style.paddingTop = originalPaddingTop;
			});

			panel.addEventListener('transitionend', function handler() {
				panel.style.height = '';
				panel.style.overflow = '';
				panel.removeEventListener('transitionend', handler);
			});
		} else {
			const originalPaddingTop = getComputedStyle(panel).paddingTop;
			panel.style.paddingTop = '0px';
			const contentHeight = panel.scrollHeight;
			panel.style.paddingTop = originalPaddingTop;

			const paddingTop = parseInt(originalPaddingTop, 10) || 0;
			const totalHeight = contentHeight + paddingTop;

			panel.style.height = totalHeight + 'px';
			panel.style.opacity = 0;

			requestAnimationFrame(() => {
				panel.style.height = '0px';
				panel.style.paddingTop = '0px';
			});

			panel.addEventListener('transitionend', function handler() {
				panel.classList.add('_hidden');
				panel.style.height = '';
				panel.style.overflow = '';
				panel.style.paddingTop = '';
				panel.removeEventListener('transitionend', handler);
			});
		}
	}

	_show(index, userInvoked) {
		this.tabTriggers[index].classList.add('_selected');
		this.tabTriggers[index].setAttribute('aria-selected', true);
		this.tabTriggers[index].removeAttribute('tabindex');

		const panel = this.tabPanels[index];
		panel.removeAttribute('tabindex');
		panel.setAttribute('aria-hidden', false);

		if (this.hasAnimation) {
			this._animatePanel(panel, true);
		} else {
			panel.classList.remove('_hidden');
		}

		if (userInvoked) this.tabTriggers[index].focus();
	}

	_hide(index, animate = true) {
		this.tabTriggers[index].classList.remove('_selected');
		this.tabTriggers[index].setAttribute('aria-selected', false);

		const panel = this.tabPanels[index];
		panel.setAttribute('tabindex', -1);
		panel.setAttribute('aria-hidden', true);

		if (this.hasAnimation && animate) {
			this._animatePanel(panel, false);
		} else {
			panel.classList.add('_hidden');
		}
	}

	selectTab(index, userInvoked) {
		if (this._isTabDisabled(index)) {
			return;
		}

		if (this.selectedTab !== null) this._hide(this.selectedTab);
		this.selectedTab = index;
		this._show(this.selectedTab, userInvoked);
	}

	destroy() {
		for (let i = 0; i < this.tabTriggersLength; i++) {
			this.tabTriggers[i].removeEventListener('click', this.clickListener);
			this.tabTriggers[i].removeEventListener('keydown', this.keydownListener);
			delete this.tabTriggers[i].index;
		}
		this.el.classList.remove('is-initialized');
	}
}

document.querySelectorAll('[data-tabs]').forEach((tab) => new Tabs(tab));
export default Tabs;
